import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {BadgeEuro, CalendarDays, Link2, MapPin, Plus} from "lucide-react";
import Button from "components/button";
import Tooltip from "components/tooltips/Tooltips";
import {InfoRow} from "components/missionDetail/MissionDetailParts";
import MissionRecruitment from "components/missionDetail/MissionRecruitment";
import SectorBadge from "components/sectorBadge/SectorBadge";
import UseContext from "hooks/useContext";
import {usePrefersReducedMotion} from "components/emptyState/glyphMotion";
import {ShimmeringCheckBadge} from "components/checkList/CheckList";
import ApiDataBase from "server";
import {dateWordShort} from "helper/helper";

const ORANGE = "#FF9600";

// One burst around the rosette: angle (deg), distance (px), colour, delay (ms). Fixed, so the
// burst looks the same at every visit.
const CONFETTI = [
    [-160, 120, ORANGE, 0], [-140, 150, "#FCD34D", 60], [-120, 110, "#34D399", 20], [-100, 160, ORANGE, 90],
    [-80, 140, "#60A5FA", 40], [-60, 120, "#FCD34D", 0], [-40, 155, ORANGE, 70], [-20, 115, "#F472B6", 30],
    [0, 135, "#34D399", 50], [20, 150, ORANGE, 10], [200, 125, "#60A5FA", 80], [160, 140, "#F472B6", 20],
    [180, 110, "#FCD34D", 60], [-180, 145, "#34D399", 40],
];

const ConfettiBurst = () => (
    <div className="pointer-events-none absolute left-1/2 top-1/2" aria-hidden="true">
        {CONFETTI.map(([angle, distance, color, delay], index) => {
            const radians = (angle * Math.PI) / 180;
            return (
                <span
                    key={index}
                    className={`absolute -ml-1 -mt-1 h-2 w-2 animate-confetti ${index % 3 === 0 ? "rounded-full" : "rounded-sm"}`}
                    style={{
                        backgroundColor: color,
                        animationDelay: `${250 + delay}ms`,
                        "--confetti-x": `${Math.round(Math.cos(radians) * distance)}px`,
                        "--confetti-y": `${Math.round(Math.sin(radians) * distance)}px`,
                        "--confetti-r": `${index % 2 ? 260 : -220}deg`,
                    }}
                />
            );
        })}
    </div>
);

/**
 * Last step of the mission wizard, once the mission is created (and the dashboard test page):
 * the mission is celebrated, summed up, its next steps shown with the mission sheet's
 * recruitment timeline, then what to do next.
 */
const MissionCreatedContent = ({missionId: id}) => {
    const navigate = useNavigate();
    const {companyData, language} = UseContext();
    const prefersReducedMotion = usePrefersReducedMotion();
    const [mission, setMission] = useState(null);
    const [companyMissions, setCompanyMissions] = useState(null);

    const t = (key, fallback) => language.getValue(`missionCreated.${key}`, fallback);
    const tFiche = (key, fallback) => language.getValue(`missionFiche.${key}`, fallback);
    const fill = (text, values) =>
        Object.entries(values).reduce((result, [name, value]) => result.replace(`{${name}}`, value ?? ''), text);

    useEffect(() => {
        if (!id) return undefined;
        let cancelled = false;
        ApiDataBase.getMissionInfo({id},
            (data) => {
                if (!cancelled) setMission(data ?? {});
            },
            (err) => {
                console.error(err);
                if (!cancelled) toast.error(t('loadError', 'Impossible de charger la mission'));
            }
        );
        return () => {
            cancelled = true;
        };
    }, [id]);

    // The rank of this mission among the company's: "your 12th mission".
    useEffect(() => {
        if (!companyData?._id) return undefined;
        let cancelled = false;
        ApiDataBase.getMissions({idC: companyData._id},
            (data) => {
                if (!cancelled) setCompanyMissions(Array.isArray(data) ? data : []);
            },
            () => {}
        );
        return () => {
            cancelled = true;
        };
    }, [companyData?._id]);

    const rank = useMemo(() => {
        if (!companyMissions || !mission?.createdAt) return null;
        const createdAt = new Date(mission.createdAt).getTime();
        return companyMissions.filter((item) => new Date(item.createdAt).getTime() <= createdAt).length || null;
    }, [companyMissions, mission?.createdAt]);

    const missionNumber = mission?.id_mission;
    const startDate = mission?.duration?.datestart;
    const endDate = mission?.duration?.dateend;
    const period = !startDate && !endDate
        ? tFiche('noDates', 'Dates non précisées')
        : dateWordShort(startDate) === dateWordShort(endDate)
            ? fill(tFiche('periodDay', 'Le {date}'), {date: dateWordShort(startDate)})
            : fill(tFiche('periodRange', 'Du {start} au {end}'), {start: dateWordShort(startDate), end: dateWordShort(endDate)});
    const address = mission?.address ?? {};
    const place = [address.postcode, address.city].filter(Boolean).join(' ') || tFiche('noLocation', 'Lieu non spécifié');
    const wage = mission?.salary?.wage_per_hour
        ? fill(tFiche('hourlyWageValue', '{amount} €/h'), {amount: mission.salary.wage_per_hour})
        : tFiche('wageUnknown', 'Selon expérience');
    const missionPublicUrl = missionNumber ? `https://jobbiz.fr/jobs/detail/?id=${missionNumber}` : null;

    const copyMissionLink = () => {
        navigator.clipboard.writeText(missionPublicUrl).then(
            () => toast.success(t('linkCopied', 'Lien de la mission copié')),
            () => toast.error(t('linkCopyError', "Le lien n'a pas pu être copié"))
        );
    };

    const rankLine = rank === 1
        ? t('firstMission', 'Votre première mission avec Jobbiz, bienvenue à bord !')
        : rank
            ? fill(t('nthMission', 'Votre {rank}ᵉ mission avec Jobbiz.'), {rank})
            : null;

    return (
        <div className="flex justify-center">
                <div className="flex w-full max-w-[640px] flex-col gap-6">
                    {/* Celebration: the rosette pops in, a burst of confetti around it. */}
                    <header className="flex flex-col items-center gap-3 text-center">
                        <div className="relative flex h-24 w-24 items-center justify-center">
                            {!prefersReducedMotion && mission ? <ConfettiBurst/> : null}
                            {/* The CheckList rosette, its glint sweeping it again and again. */}
                            <div className={mission && !prefersReducedMotion ? "animate-popIn" : ""}>
                                <ShimmeringCheckBadge size={88}/>
                            </div>
                        </div>
                        <p className="text-sm font-medium leading-5 text-yellow-500">{t('eyebrow', 'Félicitations !')}</p>
                        <h1 className="text-2xl font-bold leading-[30px] tracking-[-0.6px] text-gray-900">
                            {missionNumber
                                ? fill(t('title', 'Mission n°{number} publiée'), {number: missionNumber})
                                : t('titleNoNumber', 'Votre mission est publiée')}
                        </h1>
                        <p className="max-w-[460px] text-sm leading-5 text-gray-600">
                            {rankLine ? <span className="font-medium text-gray-900">{rankLine} </span> : null}
                            {t('description', "Elle est visible par les intérimaires dès maintenant. Nous vous prévenons dès qu'un profil est attribué.")}
                        </p>
                    </header>

                    {mission ? (
                        <>
                            <section className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h2 className="min-w-0 flex-1 text-lg font-semibold leading-6 tracking-[-0.3px] text-gray-900">
                                        {mission.title || t('recapTitle', 'Récapitulatif')}
                                    </h2>
                                    <SectorBadge activity={mission.activity} size="sm"/>
                                    {missionPublicUrl ? (
                                        <Tooltip text={t('copyLink', 'Copier le lien')} placement="top">
                                            <Button
                                                size={'ICON'}
                                                color={'SECONDARY'}
                                                onClick={copyMissionLink}
                                                ariaLabel={t('copyLink', 'Copier le lien')}
                                            >
                                                <Link2 size={18} color="#374151" aria-hidden="true"/>
                                            </Button>
                                        </Tooltip>
                                    ) : null}
                                </div>
                                <InfoRow icon={CalendarDays} label={t('period', 'Période')} value={period}/>
                                <InfoRow icon={MapPin} label={t('place', 'Lieu de travail')} value={place}/>
                                <InfoRow icon={BadgeEuro} label={t('wage', 'Taux horaire')} value={wage}/>
                            </section>

                            <section className="flex flex-col">
                                <h2 className="text-sm font-semibold leading-5 text-gray-900">{t('nextTitle', 'Et maintenant ?')}</h2>
                                <div className="-mt-2">
                                    <MissionRecruitment
                                        mission={mission}
                                        labels={{
                                            published: tFiche('recruitment.published', 'Mission publiée'),
                                            publishedOn: tFiche('recruitment.publishedOn', 'Le {date}'),
                                            recruiting: tFiche('recruitment.recruiting', 'Recrutement en cours'),
                                            searching: tFiche('recruitment.searching', 'Nous recherchons un profil pour cette mission.'),
                                            pendingOne: tFiche('recruitment.pendingOne', '{count} candidature en cours de traitement'),
                                            pendingMany: tFiche('recruitment.pendingMany', '{count} candidatures en cours de traitement'),
                                            assigned: tFiche('recruitment.assigned', 'Intérimaire attribué'),
                                            assignedHint: tFiche('recruitment.assignedHint', "Son nom s'affichera sur cette fiche."),
                                        }}
                                    />
                                </div>
                            </section>

                            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                <Button size={'LG'} color={'SECONDARY'} onClick={() => navigate('/missionTemplates')}>
                                    <Plus size={18} color="#374151" aria-hidden="true"/>
                                    {t('createAnother', 'Créer une autre mission')}
                                </Button>
                                <Button size={'LG'} color={'PRIMARY'} onClick={() => navigate(`/mission/${id}`)}>
                                    {t('seeMission', 'Voir la mission')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="flex justify-center py-10" role="status" aria-busy="true">
                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"/>
                        </div>
                    )}
                </div>
        </div>
    );
};

export default MissionCreatedContent;
